/**
 * Central API configuration
 * 
 * This file provides a consistent way to handle API URL configuration
 * across the application for both authenticated and public endpoints.
 */

// Determine if we're in a production environment
const isProduction = process.env.NODE_ENV === 'production';

// Base API URL configuration
export const API_BASE_URL = isProduction ? '' : 'http://localhost:5005';

/**
 * Get the base URL for API requests
 * 
 * @param {boolean} isRelative - If true, return a relative URL (empty string in production)
 * @returns {string} The base URL to use for API requests
 */
export const getApiBaseUrl = (isRelative = false) => {
  // In production with relative URLs, use empty string (relative to origin)
  if (isProduction && isRelative) {
    return '';
  }
  
  // Otherwise use the configured API base URL
  return API_BASE_URL;
};

/**
 * Dynamically determine API base URL for standalone HTML pages
 * 
 * @returns {string} The base URL to use for API requests
 */
export const getBrowserApiBaseUrl = () => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  
  // In production, API requests go to the same origin (no port specified)
  // In development, we use the specific port for the backend server
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // Development environment - use the specific port
    return `${protocol}//localhost:5005`;
  } else {
    // Production environment - use same origin (empty string)
    return '';
  }
};

/**
 * Function to format API endpoint URL
 * 
 * @param {string} endpoint - The API endpoint path (starting with /)
 * @param {boolean} isRelative - If true, use relative URL in production
 * @returns {string} The complete API URL
 */
export const getApiUrl = (endpoint, isRelative = false) => {
  return `${getApiBaseUrl(isRelative)}${endpoint}`;
};

// Export a browser-compatible version of this module for use in vanilla JS files
if (typeof window !== 'undefined') {
  window.apiConfig = {
    getApiBaseUrl: getBrowserApiBaseUrl,
    getApiUrl: (endpoint) => `${getBrowserApiBaseUrl()}${endpoint}`
  };
}